import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

interface CallState {
  status: 'idle' | 'calling' | 'success' | 'error';
  message: string;
  callSid?: string;
}

export default function TestCallWidget() {
  const [callState, setCallState] = useState<CallState>({ status: 'idle', message: '' });
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [loadingPhone, setLoadingPhone] = useState(true);

  useEffect(() => {
    loadPhoneNumber();
  }, []);

  const loadPhoneNumber = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .from('users')
        .select('phone_number')
        .eq('id', user.id)
        .maybeSingle();

      if (data?.phone_number) {
        setPhoneNumber(data.phone_number);
      }
    } catch {
      // Silently fail — user may not have phone set yet
    } finally {
      setLoadingPhone(false);
    }
  };

  const handleTestCall = async () => {
    if (!phoneNumber) {
      setCallState({ status: 'error', message: 'Please set your phone number in Settings first.' });
      return;
    }

    setCallState({ status: 'calling', message: 'Initiating test call...' });

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setCallState({ status: 'error', message: 'Session expired. Please refresh the page.' });
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/signalwire-make-call`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ user_id: user?.id }),
        },
      );

      const result = await response.json();

      if (!response.ok) {
        const detail =
          result.details?.message || result.details?.error || (typeof result.details === 'string' ? result.details : '');
        setCallState({
          status: 'error',
          message: detail ? `${result.error}: ${detail}` : result.error || 'Failed to make call',
        });
        return;
      }

      setCallState({
        status: 'success',
        message: `Calling ${phoneNumber}... Pick up your phone!`,
        callSid: result.call_sid,
      });
    } catch (err) {
      setCallState({
        status: 'error',
        message: 'Network error. Please try again.',
      });
    }
  };

  const statusColors = {
    idle: 'text-foreground-600',
    calling: 'text-primary-600',
    success: 'text-emerald-600',
    error: 'text-red-500',
  };

  const statusIcons = {
    idle: 'ri-phone-line',
    calling: 'ri-loader-4-line',
    success: 'ri-check-double-line',
    error: 'ri-close-circle-line',
  };

  return (
    <div className="p-5 rounded-xl bg-surface dark:bg-midnight-card border border-gold/30 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <span className="w-8 h-8 rounded-lg bg-gold/15 flex items-center justify-center">
            <i className="ri-phone-fill text-gold text-sm" />
          </span>
          <h3 className="text-sm font-heading font-semibold text-foreground-950">Test Call</h3>
        </div>
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 font-medium whitespace-nowrap">
          SignalWire
        </span>
      </div>

      {loadingPhone ? (
        <div className="flex items-center gap-2 text-xs text-foreground-400">
          <i className="ri-loader-4-line animate-spin" />
          Loading...
        </div>
      ) : !phoneNumber ? (
        <div className="space-y-3">
          <p className="text-xs text-foreground-500">
            No phone number set. Add one in Settings to test the call system.
          </p>
          <Link
            to="/settings"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-primary-600 hover:text-primary-700 transition cursor-pointer whitespace-nowrap"
          >
            <i className="ri-settings-3-line" />
            Go to Settings
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-xs text-foreground-500">
            <i className="ri-smartphone-line" />
            <span>{phoneNumber}</span>
          </div>

          <button
            onClick={handleTestCall}
            disabled={callState.status === 'calling'}
            className="btn-primary w-full flex items-center justify-center gap-2 py-3 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className={`${statusIcons[callState.status]} ${callState.status === 'calling' ? 'text-sm' : ''}`} />
            </span>
            {callState.status === 'calling' ? 'Calling...' : 'Call Me Now'}
          </button>

          <p className="text-center text-[11px] text-foreground-400">Test your call settings</p>

          {callState.message && callState.status !== 'idle' && (
            <p className={`text-xs ${statusColors[callState.status]} flex items-start gap-1`}>
              <span className="w-3.5 h-3.5 flex items-center justify-center mt-px flex-shrink-0">
                <i className={statusIcons[callState.status]} />
              </span>
              {callState.message}
            </p>
          )}

          {callState.status === 'success' && callState.callSid && (
            <p className="text-[10px] text-foreground-400 font-mono truncate">
              SID: {callState.callSid}
            </p>
          )}
        </div>
      )}
    </div>
  );
}